import { useEffect, useMemo, useState } from "react"
import {
  CurrentMonth,
  DateButton,
  DateButtonText,
  HeaderWrapper,
  HighlightDot,
  NavigateBox,
  NavigateButton,
  StyledDatePicker,
  StyledTd,
  StyledTh,
  StyledThR,
  StyledTr,
  TodayButton,
} from "./styled"
import dayjs, { Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import timeZone from "dayjs/plugin/timezone"
import LeftIcon from "./Icons/LeftIcon"
import RightIcon from "./Icons/RightIcon"
import isBetween from "dayjs/plugin/isBetween"

dayjs.extend(utc)
dayjs.extend(timeZone)
dayjs.extend(isBetween)

interface IHeaderProps {
  month: Dayjs
  setMonth: (month: Dayjs) => void
  backToToday: () => void
}

const Header = (props: IHeaderProps) => {
  const { month, setMonth, backToToday } = props
  const isDiffMonth = !dayjs().isSame(month, "month")

  return (
    <HeaderWrapper>
      <CurrentMonth>{month.format("MMMM YYYY")}</CurrentMonth>
      <NavigateBox>
        {isDiffMonth && <TodayButton onClick={backToToday}>Today</TodayButton>}

        <NavigateButton onClick={() => setMonth(month.subtract(1, "month"))}>
          <LeftIcon />
        </NavigateButton>
        <NavigateButton onClick={() => setMonth(month.add(1, "month"))}>
          <RightIcon />
        </NavigateButton>
      </NavigateBox>
    </HeaderWrapper>
  )
}

const getArraySeven = () => Array(7).fill(0)

const TableHead = () => {
  return (
    <thead>
      <StyledThR>
        {getArraySeven().map((_, index) => (
          <StyledTh key={index}>{dayjs().day(index).format("ddd")}</StyledTh>
        ))}
      </StyledThR>
    </thead>
  )
}

interface IDateProps {
  day: Dayjs
  month: Dayjs
  start?: Dayjs
  end?: Dayjs
  onSelectDate?: (date: Dayjs) => void
  tmpStartDate?: Dayjs
  isHighlightDate?: (date: Dayjs) => boolean
  isDisabledDate?: (date: Dayjs) => boolean
}

const Date = (props: IDateProps) => {
  const {
    day,
    month,
    start,
    onSelectDate,
    end,
    tmpStartDate,
    isHighlightDate,
    isDisabledDate,
  } = props
  const isInDifferentMonth =
    day.isBefore(month.startOf("month")) || day.isAfter(month.endOf("month"))

  const isToday = day.isSame(dayjs().startOf("day"))

  const isStartDate = tmpStartDate && tmpStartDate.startOf("day").isSame(day)

  const startPeriod = tmpStartDate ?? start
  const endPeriod = tmpStartDate ? undefined : end

  const isStartPeriod = !!startPeriod && startPeriod.isSame(day, "day")
  const isEndPeriod = !!endPeriod && endPeriod.isSame(day, "day")
  const isInSelectedRange =
    !!startPeriod &&
    !!endPeriod &&
    !!day.isBetween(startPeriod, endPeriod, "day", "[]")

  const isDisabled = isDisabledDate?.(day) ?? false

  return (
    <StyledTd
      $isInSelectedRange={isInSelectedRange}
      $isStartPeriod={isStartPeriod}
      $isEndPeriod={isEndPeriod}
    >
      <DateButton
        $isDisabled={isDisabled}
        $isInCurrentMonth={!isInDifferentMonth}
        $isSelected={isStartPeriod}
        $isToday={isToday}
        $isInSelectedRange={isInSelectedRange}
        $isHighlightDate={isHighlightDate?.(day)}
        onClick={() => {
          if (!isDisabled) {
            onSelectDate?.(day)
          }
        }}
      >
        <DateButtonText
          className="date_btn__text"
          $isDisabled={isDisabled}
          $isInCurrentMonth={!isInDifferentMonth}
          $isSelected={!!isStartDate}
          $isToday={isToday}
          $isInSelectedRange={isInSelectedRange}
        >
          {day.get("date")}
        </DateButtonText>

        {isInSelectedRange && isHighlightDate?.(day) && <HighlightDot />}
      </DateButton>
    </StyledTd>
  )
}

interface IWeekRowProps {
  sunday: Dayjs
  start?: Dayjs
  end?: Dayjs
  month: Dayjs
  tmpStartDate?: Dayjs
  onSelectDate?: (date: Dayjs) => void
  isHighlightDate?: (date: Dayjs) => boolean
  isDisabledDate?: (date: Dayjs) => boolean
}

const WeekRow = (props: IWeekRowProps) => {
  const {
    sunday,
    start,
    end,
    onSelectDate,
    month,
    tmpStartDate,
    isHighlightDate,
    isDisabledDate,
  } = props
  return (
    <StyledTr $isActive={false}>
      {getArraySeven().map((_, index) => (
        <Date
          isDisabledDate={isDisabledDate}
          month={month}
          key={index}
          day={sunday.add(index, "day")}
          start={start}
          end={end}
          onSelectDate={onSelectDate}
          tmpStartDate={tmpStartDate}
          isHighlightDate={isHighlightDate}
        />
      ))}
    </StyledTr>
  )
}

interface IRangePickerProps {
  start?: Dayjs
  end?: Dayjs
  onChange?: ({ start, end }: { start: Dayjs; end?: Dayjs }) => void
  isSingleMode?: boolean
  isHighlightDate?: (date: Dayjs) => boolean
  isDisabledDate?: (date: Dayjs) => boolean
}

const RangePicker = (props: IRangePickerProps) => {
  const {
    start,
    end,
    onChange,
    isSingleMode,
    isHighlightDate,
    isDisabledDate,
  } = props

  const [month, setMonth] = useState(dayjs().startOf("month"))
  const startDate = useMemo(() => month.startOf("week"), [month])

  const [tmpStartDate, setTmpStartDate] = useState<Dayjs | undefined>()

  const onSelectDate = (date: Dayjs) => {
    if (isSingleMode) {
      onChange?.({ start: date })
      return
    }

    if (!tmpStartDate) {
      setTmpStartDate(date)
    } else {
      if (date.isBefore(tmpStartDate)) {
        setTmpStartDate(date)
      } else {
        onChange?.({ start: tmpStartDate, end: date })
        setTmpStartDate(undefined)
      }
    }
  }

  useEffect(() => {
    if (start && start.month() !== month.month()) {
      setMonth(start.startOf("month"))
    }
  }, [start])

  const backToToday = () => {
    setMonth(dayjs().startOf("month"))
  }

  const weeks = useMemo(() => {
    const weeks = []
    let done = false
    let sunday = startDate
    let monthIndex = month.month()
    let count = 0

    while (!done) {
      weeks.push(
        <WeekRow
          key={sunday.toString()}
          sunday={sunday}
          start={start}
          end={end}
          month={month}
          onSelectDate={onSelectDate}
          tmpStartDate={tmpStartDate}
          isHighlightDate={isHighlightDate}
          isDisabledDate={isDisabledDate}
        />,
      )
      sunday = sunday.add(1, "week")
      done = count++ > 2 && monthIndex !== sunday.month()
      monthIndex = sunday.month()
    }

    return weeks
  }, [month, start, end, tmpStartDate])

  return (
    <StyledDatePicker>
      <Header month={month} setMonth={setMonth} backToToday={backToToday} />
      <table>
        <TableHead />
        <tbody>{weeks}</tbody>
      </table>
    </StyledDatePicker>
  )
}

export default RangePicker
